import { GraphQLFormattedError } from "graphql";

const resolvedInComponentErrorsList = [
  /apartment\/\w*\/climate/i,
  // Add paths for the known "unavailable" enum issues
  /apartment\/bathroomA\/lighting\/lightsAutoOff/i,
  /apartment\/kitchenA\/lighting\/lightsCircadian/i,
  // Add general paths for lighting properties that might have "unavailable" values
  /apartment\/.*\/lighting\/.*/i,
  // Ignore update last login request errors
  /updateUserLastLogin/
]

/**
 * This method check, is error will processed in target component to prevent app blocking
 */
export function isErrorResolvedInComponent(error: GraphQLFormattedError | undefined): boolean {
  if (!error?.path) return false;

  const path = error.path.join('/');
  const result = resolvedInComponentErrorsList.some(checkPath => !!path.match(checkPath));
  return result;
}


// Attempt by BG to prevent the app from comletely not working if there is an unknown error state
/**
 * Determines if an error is related to enum validation
 */
export function isEnumValidationError(error: GraphQLFormattedError | undefined): boolean {
  if (!error?.message) return false;

  // Check if error message indicates enum validation issue
  const isEnumError = error.message.includes("Enum") && error.message.includes("cannot represent value");
  return isEnumError;
}

/**
 * Checks if the error is specific to the initial data load and should be ignored
 */
export function isInitialDataLoadError(error: GraphQLFormattedError | undefined): boolean {
  // If we add more initial load errors, add them here
  return isEnumValidationError(error);
}

/**
 * Checks if the error is a "not iterable" error which is usually a runtime issue
 * that can be handled gracefully
 */
export function isNotIterableError(error: any): boolean {
  if (!error?.message) return false;
  
  if (error.message.includes("is not iterable")) {
    console.warn("Caught 'not iterable' error that will be handled gracefully:", error.message);
    return true;
  }
  
  return false;
}

/**
 * Checks if the error is a JavaScript runtime error that shouldn't cause app failure
 * Specifically looking for errors like 'undefined is not an object'
 */
export function isJavaScriptRuntimeError(error: any): boolean {
  if (!error) return false;

  // Check for the specific error seen in logs
  if (error.message && (
    error.message.includes("undefined is not an object") ||
    error.message.includes("toString")
  )) {
    console.warn("Caught non-critical JavaScript runtime error:", error.message);
    return true;
  }

  return false;
}

/**
 * Checks if the error is a backend/Lambda infrastructure error that shouldn't block the app
 * These are server-side issues that the app should handle gracefully
 */
export function isBackendInfrastructureError(error: any): boolean {
  if (!error?.message) return false;

  const message = error.message;

  // Lambda function not found or invocation errors
  if (message.includes("Function not found") ||
      message.includes("Lambda") ||
      message.includes("ResourceNotFoundException")) {
    console.warn("Caught backend infrastructure error (app will continue):", message);
    return true;
  }

  return false;
}

/**
 * Check all errors to determine if they will be processed in the app
 * or if they should cause the app to go to error state
 */
export function isAllErrorsWillBeProcessedInApp(errors: readonly GraphQLFormattedError[] | undefined): boolean {
  if (!errors?.length) return true;

  // Log all errors to help diagnose issues
  // console.log(`Checking ${errors.length} GraphQL errors:`, JSON.stringify(errors.map(e => ({
  //   message: e.message,
  //   path: e.path
  // }))));

  // Check each error if it should be handled in the component or is a known allowed error
  const allErrorsProcessed = errors.every((error) => {
    const canBeProcessed = isErrorResolvedInComponent(error) ||
      isEnumValidationError(error) ||
      isInitialDataLoadError(error) ||
      isJavaScriptRuntimeError(error as any) ||
      isNotIterableError(error as any) ||
      isBackendInfrastructureError(error as any);
      
    if (!canBeProcessed) {
      console.warn(`Unhandled error that will cause app to block:`, error.message);
    }

    return canBeProcessed;
  });
  return allErrorsProcessed;
}
